import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  deInvoiceTermsUrl,
  supportedCountryCodes,
} from '@app/common/de-common';
import {
  availableCountryCodes,
  setCountryFlag,
} from '@app/common/select-country';
import { SelectorItem } from '@app/component/selector/selector.component';
import {
  Market,
  PaymentMethod,
} from '@app/model/payment-request/payment-request.dto';
import { HeaderService } from '@app/service/header/header.service';
import { ValuePipe } from '@app/shared/pipe/value.pipe';
import { Subscription } from 'rxjs/internal/Subscription';
import {
  CheckoutStatus,
  StateService,
} from 'src/app/service/state/state.service';
import {
  EmailAndPhone,
  StandardCheckoutResponse,
  UserRegistrationDto,
  UserService,
} from 'src/app/service/user-service/user.service';
import { email_validation_regexp } from 'src/assets/val/regexp';
import {
  Color,
  formatPhoneNumber,
  Item,
  phoneNumberValidator,
} from 'web-component-library/projects/component-library/src/public-api';
import { RouteComponent } from '../../route.component';
import { DeviceDetectorService } from 'ngx-device-detector';
import { LoanOption } from '@app/route/shared/LoanOption';
import { DeLoanOptionService } from '@app/service/de-loan-option/de-loan-option.service';

enum View {
  DEFAULT = 'DEFAULT',
  NOT_FOUND = 'NOT_FOUND',
  CONFIRM_EMAIL = 'CONFIRM_EMAIL',
}
@Component({
  selector: 'app-de-email-and-phone-registration',
  templateUrl: './de-email-and-phone-registration.component.html',
  styleUrls: [
    '../../page-shared.css',
    './de-email-and-phone-registration.component.scss',
  ],
})
export class DeEmailAndPhoneRegistrationComponent
  extends RouteComponent
  implements OnInit, OnDestroy
{
  form: FormGroup;
  confirmEmailForm: FormGroup;
  loading = true;
  showError = false;
  currentView = View.DEFAULT;
  navigationInProgress = false;
  headingText: string;
  infoText: string;
  imgSrc: string;
  selectorItems: SelectorItem[];
  modalActive: boolean = false;
  modalLogo = '';
  isMobile: boolean;
  preExistingContactInfo: UserRegistrationDto;
  View = View;
  Color = Color;
  readonly AVAILABLECOUNTRYCODES = availableCountryCodes;
  selectedCountry: Item = null;
  formSubscription: Subscription;
  inputSize: 'small' | 'medium' | 'large';
  currentLoanTermsStr: string;

  shouldDisabledEmailInput: boolean = false;
  shouldDisabledPhoneInput: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    protected router: Router,
    private valuePipe: ValuePipe,
    protected route: ActivatedRoute,
    protected stateService: StateService,
    protected headerService: HeaderService,
    private userService: UserService,
    private deviceDetectorService: DeviceDetectorService,
    private loanOptionService: DeLoanOptionService
  ) {
    super(router, route, stateService, headerService);
  }

  async ngOnInit(): Promise<void> {
    if (
      this.state.status === CheckoutStatus.EXPIRED ||
      this.state.status === CheckoutStatus.CANCELLED
    ) {
      return;
    }

    // Set AT flag as default is market is at, else use DE flag
    if (this.state.market === Market.AT) {
      this.selectedCountry = this.AVAILABLECOUNTRYCODES[0];
    } else {
      this.selectedCountry = this.AVAILABLECOUNTRYCODES[2];
    }

    this.isMobile = this.deviceDetectorService.isMobile();
    try {
      this.preExistingContactInfo =
        await this.stateService.getPreExistingContactInfo();
    } catch (error) {
      this.handleError(error);
    }
    this.shouldDisabledEmailInput =
      !this.preExistingContactInfo.email.editable &&
      !!this.preExistingContactInfo.email.value;
    this.shouldDisabledPhoneInput =
      !this.preExistingContactInfo.phoneNumber.editable &&
      !!this.preExistingContactInfo.phoneNumber.value;

    this.preExistingContactInfo.phoneNumber.value = this.appendCountryCode(
      this.preExistingContactInfo.phoneNumber.value
    );

    this.selectedCountry = setCountryFlag(
      this.preExistingContactInfo.phoneNumber.value,
      this.selectedCountry
    );

    this.form = this.formBuilder.group({
      email: [
        {
          value: this.preExistingContactInfo.email.value || '',
          disabled: !this.preExistingContactInfo.email.editable,
        },
        [Validators.pattern(email_validation_regexp), Validators.required],
      ],
      phoneNumber: [
        {
          value:
            this.preExistingContactInfo.phoneNumber.value ||
            this.selectedCountry?.value ||
            '',
          disabled: !this.preExistingContactInfo.phoneNumber.editable,
        },
        [
          phoneNumberValidator(supportedCountryCodes, this.state.market),
          Validators.required,
        ],
      ],
    });
    this.loading = false;

    this.formSubscription =
      this.form.controls.phoneNumber.valueChanges.subscribe((value) => {
        this.selectedCountry = setCountryFlag(value, this.selectedCountry);
      });

    this.modalLogo = 'assets/img/Zaver_Logotype_Circle_Coal_DE.svg';

    this.selectorItems = [
      {
        tab: 'Ihr Kauf',
        title: 'Zusammenfassung',
        lineItems: this.state.paymentInformation?.lineItems,
        totalValue: this.stateService.getCustomTaxTypeText(
          `Gesamtbetrag inkl. MwSt: ${this.valuePipe.transform(
            this.state.totalCheckoutValue
          )}`,
          this.state.checkoutCustomizations
        ),
      },
    ];

    if (
      this.state.paymentInformation?.paymentMethod === PaymentMethod.INVOICE ||
      this.state.paymentInformation?.paymentMethod === PaymentMethod.INVOICE_MCO
    ) {
      this.selectorItems = [
        ...this.selectorItems,
        {
          tab: 'Zahlungsbedingungen',
          title: 'Bedingungen für den Kauf auf Rechnung',
          descriptions: [
            {
              text:
                'Wenn Sie sich für die Zahlung per Rechnung entscheiden, bieten wir Ihnen die Möglichkeit, innerhalb von ' +
                this.getDueDateString() +
                ' Tagen ab dem Datum der Rechnungsstellung zu zahlen.',
            },
          ],
          links: [
            {
              text: 'Allgemeine Zahlungsbedingungen',
              onClick: () => this.openURLinNewWindow(deInvoiceTermsUrl),
            },
            {
              ...(this.state.checkoutCustomizations.merchantTermsUrl !== null &&
                this.state.checkoutCustomizations.merchantName !== null && {
                  text: this.stateService.getCustomTermsUrlText(
                    this.state.checkoutCustomizations.merchantName + ' AGB',
                    this.state.checkoutCustomizations
                  ),
                  onClick: () =>
                    this.openURLinNewWindow(
                      this.state.checkoutCustomizations.merchantTermsUrl
                    ),
                }),
            },
          ],
        },
      ];
    } else if (
      this.state.loanOptions &&
      this.state.loanOptions.length > 0 &&
      this.state.loanOptions[0]?.termId
    ) {
      const selectedLoanOption = this.state.loanOptions.find(
        (loanOption: LoanOption) => loanOption.termId
      );

      if (selectedLoanOption) {
        this.currentLoanTermsStr = this.loanOptionService.buildLoanTermsString(
          selectedLoanOption[0],
          this.state.totalCheckoutValue
        );

        this.selectorItems = [
          ...this.selectorItems,
          {
            tab: 'Zahlungsbedingungen',
            title: 'Bedingungen für den Kauf auf Raten',
            descriptions: [{ text: this.currentLoanTermsStr }],
            links: [
              {
                text: 'Bedingungen für Teilzahlungen',
                onClick: () =>
                  this.openURLinNewWindow(selectedLoanOption.loanTermsURL),
              },
              {
                ...(this.state.checkoutCustomizations.merchantTermsUrl !==
                  null &&
                  this.state.checkoutCustomizations.merchantName !== null && {
                    text: this.stateService.getCustomTermsUrlText(
                      this.state.checkoutCustomizations.merchantName + ' AGB',
                      this.state.checkoutCustomizations
                    ),
                    onClick: () =>
                      this.openURLinNewWindow(
                        this.state.checkoutCustomizations.merchantTermsUrl
                      ),
                  }),
              },
              {
                ...(this.state.market === Market.AT && {
                  text: 'Europäische Standardinformationen für Kreditierungen nach dem Verbraucherkreditgesetz',
                  onClick: () =>
                    this.openURLinNewWindow(selectedLoanOption.secciURL),
                }),
              },
            ],
          },
        ];
      }
    }
  }

  appendCountryCode(phoneNumber: string): string {
    const countryCodes = {
      [Market.DE]: '+49 ',
      [Market.AT]: '+43 ',
    };

    if (
      phoneNumber?.length > 1 &&
      phoneNumber.slice(0, 1) !== '+' &&
      phoneNumber.slice(0, 2) !== '00'
    ) {
      return countryCodes[this.state.market] + phoneNumber;
    } else {
      return phoneNumber;
    }
  }

  openURLinNewWindow(url: string): void {
    window.open(url, '_blank');
  }

  ngOnDestroy() {
    this.formSubscription && this.formSubscription.unsubscribe();
  }

  setCountry(selected) {
    this.form.controls.phoneNumber.setValue(selected.value);
  }

  async navigateToAccountRestoration(): Promise<void> {
    try {
      await this.userService.sendAccountRestorationEmail();
      await this.navigate();
    } catch (err) {
      this.handleError(err);
    }
  }

  setCurrentView(view: View): void {
    this.currentView = view;
  }

  async registerUserData(formData: EmailAndPhone): Promise<void> {
    await this.userService
      .registerEmailAndPhone(formData)
      .then((resp: StandardCheckoutResponse) => {
        if (resp.success) {
          this.navigate();
        } else {
          this.setCurrentView(View.NOT_FOUND);
        }
      });
  }

  async handleCtaClick(): Promise<void> {
    this.navigationInProgress = true;

    try {
      if (this.form.invalid) {
        this.showError = true;
      } else {
        const formData: EmailAndPhone = {
          email: this.form.controls.email.value.trim().toLowerCase(),
          phoneNumber: formatPhoneNumber(
            this.form.controls.phoneNumber.value,
            this.state.market
          ),
        };

        if (
          this.preExistingContactInfo?.email?.value &&
          this.preExistingContactInfo?.email?.value ===
            this.form.controls.email.value.trim().toLowerCase()
        ) {
          await this.registerUserData(formData);
        } else {
          await this.userService
            .checkExistingUser(formData)
            .then(async (resp: StandardCheckoutResponse) => {
              if (resp.success) {
                await this.registerUserData(formData);
              } else {
                this.confirmEmailForm = this.formBuilder.group(
                  {
                    email: [
                      {
                        value: this.form.controls.email.value,
                        disabled: true,
                      },
                      [
                        Validators.pattern(email_validation_regexp),
                        Validators.required,
                      ],
                    ],
                    confirmEmail: [
                      '',
                      [
                        Validators.pattern(email_validation_regexp),
                        Validators.required,
                      ],
                    ],
                  },
                  {
                    validators: [confirmEmailValidation],
                  }
                );
                this.showError = false;
                this.currentView = View.CONFIRM_EMAIL;
              }
            });
        }
      }
    } catch (error) {
      this.handleError(error);
    }
    this.navigationInProgress = false;
  }

  async confirmEmail(): Promise<void> {
    this.navigationInProgress = true;

    try {
      if (this.confirmEmailForm.valid) {
        const formData: EmailAndPhone = {
          email: this.form.controls.email.value.trim().toLowerCase(),
          phoneNumber: formatPhoneNumber(
            this.form.controls.phoneNumber.value,
            this.state.market
          ),
        };
        const registerResp = await this.userService.registerEmailAndPhone(
          formData
        );

        if (registerResp.success) {
          await this.navigate();
        }
      } else {
        this.showError = true;
      }
    } catch (error) {
      this.handleError(error);
    }
    this.navigationInProgress = false;
  }

  activateModal() {
    this.modalActive = true;
  }

  deactivateModal() {
    this.modalActive = false;
  }

  getDueDateString(): string {
    if (this.state.loanOptions?.length) {
      return String(this.state.loanOptions[0].numberOfDaysUntilFinalDueDate);
    }

    return '14';
  }

  getTokenizedOnboardingText(): string {
    if (
      this.state?.zaverProduct !== 'TOKENIZED_ONBOARDING' ||
      !this.state?.checkoutCustomizations?.merchantName
    ) {
      return;
    }

    return this.state?.checkoutCustomizations?.merchantName + ' AGB';
  }

  getTokenizedOnboardingUrl(): string {
    if (
      this.state?.zaverProduct !== 'TOKENIZED_ONBOARDING' ||
      !this.state?.checkoutCustomizations?.merchantTermsUrl
    ) {
      return;
    }

    return this.state?.checkoutCustomizations?.merchantTermsUrl;
  }

  async routeToCancelUrl(): Promise<void> {
    this.navigationInProgress = true;
    try {
      const resp = await this.userService.cancelPayment();
      this.navigationInProgress = false;
      if (!resp.error) {
        window.location.href = this.state.merchantUrls?.onCancelUrl;
      }
    } catch (error) {
      console.log(error);
    }
  }
}

const confirmEmailValidation: ValidatorFn = (
  control: AbstractControl
): ValidationErrors => {
  const email = control.get('email');
  const confirmEmail = control.get('confirmEmail');

  return email &&
    confirmEmail &&
    email.value.toLowerCase() !== confirmEmail.value.toLowerCase()
    ? { confirmEmail: true }
    : null;
};
